import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  LinearProgress,
} from '@mui/material';
import { statsService, TrailProgressResponse } from '../services/statsService';

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * Em que dia cada pessoa está em cada trilha e quais dias precisam de vídeo
 * primeiro (o dia atual e os próximos de quem ainda não terminou).
 */
const TrailProgress: React.FC = () => {
  const [windowDays, setWindowDays] = useState(14);
  const [windowInput, setWindowInput] = useState('14');
  const [data, setData] = useState<TrailProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (days: number) => {
    setLoading(true);
    setError('');
    try {
      setData(await statsService.getTrailProgress(days));
    } catch {
      setError('Não foi possível carregar o progresso das trilhas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(windowDays);
  }, [load, windowDays]);

  const applyWindow = () => {
    const value = Number(windowInput);
    if (Number.isInteger(value) && value >= 1 && value <= 60) {
      setWindowDays(value);
    } else {
      setWindowInput(String(windowDays));
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Progresso nas trilhas
          </Typography>
          <Typography color="text.secondary">
            Em que dia cada pessoa está e quais vídeos subir primeiro.
          </Typography>
        </Box>
        <TextField
          label="Próximos dias"
          type="number"
          size="small"
          value={windowInput}
          onChange={(e) => setWindowInput(e.target.value)}
          onBlur={applyWindow}
          onKeyDown={(e) => e.key === 'Enter' && applyWindow()}
          inputProps={{ min: 1, max: 60 }}
          InputLabelProps={{ shrink: true }}
          helperText="Janela de dias por pessoa (1 a 60)"
          sx={{ width: 190 }}
        />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {loading && !data && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {data?.trails.map((trail) => (
        <Card key={trail.id} sx={{ mb: 3, borderRadius: 3 }}>
          {loading && <LinearProgress />}
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight={700}>
                {trail.title}
              </Typography>
              {trail.isPremium && <Chip label="Premium" size="small" color="warning" />}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                label={`${trail.daysWithVideo} de ${trail.totalDays} dias com vídeo`}
                color={trail.daysWithVideo === trail.totalDays ? 'success' : 'default'}
                variant="outlined"
              />
              <Chip
                label={`${trail.activeUsers} ${trail.activeUsers === 1 ? 'pessoa fazendo' : 'pessoas fazendo'}`}
                variant="outlined"
              />
            </Box>

            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Vídeos para subir primeiro
            </Typography>
            {trail.missingSoon.length === 0 ? (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nenhum vídeo faltando para quem está fazendo esta trilha nos próximos{' '}
                {data.window} dias.
              </Typography>
            ) : (
              <Box
                data-testid={`missing-${trail.id}`}
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}
              >
                {trail.priorityDays
                  .filter((d) => !d.hasVideo)
                  .map((d) => (
                    <Chip
                      key={d.day}
                      label={`Dia ${d.day}`}
                      color="error"
                      size="small"
                      title={`${d.users} ${d.users === 1 ? 'pessoa precisa' : 'pessoas precisam'} deste dia em breve`}
                    />
                  ))}
              </Box>
            )}

            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Quem está fazendo
            </Typography>
            {trail.users.length === 0 ? (
              <Typography color="text.secondary">Ninguém começou esta trilha ainda.</Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>E-mail</TableCell>
                      <TableCell>Plano</TableCell>
                      <TableCell align="right">Dia atual</TableCell>
                      <TableCell>Último acesso</TableCell>
                      <TableCell>Próximo dia libera</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trail.users.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          {user.isPremium ? (
                            <Chip label="Premium" size="small" color="warning" />
                          ) : (
                            <Chip label="Grátis" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {user.completed ? <Chip label="Terminou" size="small" color="success" /> : user.currentDay}
                        </TableCell>
                        <TableCell>{formatDate(user.lastViewedAt)}</TableCell>
                        <TableCell>{user.completed ? '—' : formatDate(user.nextUnlockAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default TrailProgress;
